import React from "react";

function quartalVonMonat(monatIndex) {
    return Math.floor(monatIndex / 3) + 1;
}

/**
 * Baut Gruppen zusammenhängender Spalten mit gleichem Schlüssel (Jahr bzw.
 * Jahr+Quartal) - rein über den Schlüssel, OHNE eine zusätzliche Grenze an
 * der aktuellen Woche zu erzwingen (Befund Sichtprüfung 3 #S1: eine
 * erzwungene Grenze zerriss ein Quartal in bis zu drei Zellen mit
 * demselben Text nebeneinander - eine davon oft so breit, dass ihre
 * zentrierte Beschriftung beim waagerechten Scrollen aus dem sichtbaren
 * Bereich lief und wie eine leere Zelle wirkte). Jedes Quartal/Jahr bleibt
 * dadurch ein einziges, durchgehendes Band mit genau einer Beschriftung.
 */
function gruppenBauen(spalten, schluesselVon, labelVon) {
    const gruppen = [];
    spalten.forEach((spalte) => {
        const schluessel = schluesselVon(spalte);
        const letzte = gruppen[gruppen.length - 1];
        if (letzte && letzte.schluessel === schluessel) {
            letzte.anzahl += 1;
        } else {
            gruppen.push({schluessel, anzahl: 1, label: labelVon(spalte)});
        }
    });
    return gruppen;
}

/**
 * Zwei Zeitleisten-Zeilen (Jahr, Quartal) über den Kalenderwochen-Spalten
 * einer breiten Tabelle (Rückmeldung T6: "einen Zeitstrahl sehen, wo man
 * das Quartal und die Jahre sehen kann") - gemeinsamer Baustein für
 * Lieferfähigkeit UND Lieferplan. Jedes Jahr/Quartal ist EIN durchgehendes
 * Band (siehe gruppenBauen); die Beschriftung sitzt in einer eigenen,
 * waagerecht mitscrollenden Zelle direkt hinter den fixierten Vorspalten
 * (`position: sticky; left`), damit sie auch bei einem sehr breiten Band
 * (z. B. ein ganzes Quartal mit 13 Wochen) sichtbar bleibt, statt in der
 * Mitte des - oft nicht komplett sichtbaren - Bandes zu zentrieren und aus
 * dem Bild zu laufen (Befund Sichtprüfung 3 #S1). Die aktuelle Woche wird
 * nicht mehr hier, sondern in der KW-Spalte selbst hervorgehoben (siehe
 * LieferfaehigkeitTabelle.jsx/LieferplanTabelle.jsx).
 *
 * Erwartet eine feste Spaltenbreite je Woche (`wocheBreite`) - die
 * tatsächliche Spaltenbreite muss über ein `<colgroup>` in der Tabelle
 * festgelegt sein (sonst bestimmt bei `table-layout: fixed` ausgerechnet
 * DIESE erste Kopfzeile mit ihren breiten, gruppierten Zellen die
 * Spaltenbreiten - und die eigentlichen KW-Spalten darunter würden falsch
 * ausgerichtet).
 *
 * @param {Array<{schluessel: string, montag: Date|null}>} spalten
 * @param {number} vorspaltenAnzahl - Anzahl der festen vorderen Spalten (Platzhalter-Zelle).
 * @param {number} vorspaltenBreite - Summe ihrer Breiten in px (für die sticky Platzhalter-Zelle und den Beschriftungs-Versatz).
 */
export default function KwZeitleiste({spalten, vorspaltenAnzahl, vorspaltenBreite, topJahr = 0, topQuartal = 22}) {
    if (!spalten || spalten.length === 0) {
        return null;
    }

    const jahrGruppen = gruppenBauen(
        spalten,
        (s) => (s.montag ? String(s.montag.getFullYear()) : "unbekannt"),
        (s) => (s.montag ? String(s.montag.getFullYear()) : "Woche offen")
    );
    const quartalGruppen = gruppenBauen(
        spalten,
        (s) => (s.montag ? `${s.montag.getFullYear()}-${quartalVonMonat(s.montag.getMonth())}` : "unbekannt"),
        (s) => (s.montag ? `Q${quartalVonMonat(s.montag.getMonth())} ${s.montag.getFullYear()}` : "–")
    );

    const platzhalterStil = (top) => ({
        position: "sticky",
        left: 0,
        top,
        zIndex: 5,
        background: "#fff",
        width: vorspaltenBreite,
        minWidth: vorspaltenBreite,
    });

    // Sitzt in einer eigenen Zelle direkt hinter den fixierten Vorspalten und
    // scrollt waagerecht mit - dadurch bleibt sie sichtbar, egal wie breit
    // (wie viele Wochen) das umgebende Band ist.
    const beschriftungStil = {
        position: "sticky",
        left: vorspaltenBreite + 8,
        display: "inline-block",
        whiteSpace: "nowrap",
    };

    return (
        <>
            <tr style={{height: 22}}>
                <th colSpan={vorspaltenAnzahl} style={platzhalterStil(topJahr)} aria-hidden="true" />
                {jahrGruppen.map((gruppe, index) => (
                    <th
                        key={`jahr-${index}`}
                        colSpan={gruppe.anzahl}
                        className="text-start text-uppercase"
                        style={{
                            position: "sticky",
                            top: topJahr,
                            zIndex: 1,
                            background: "#f8f9fa",
                            borderLeft: index > 0 ? "1px solid #ced4da" : undefined,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "#495057",
                        }}
                    >
                        <span style={beschriftungStil}>{gruppe.label}</span>
                    </th>
                ))}
            </tr>
            <tr style={{height: 22}}>
                <th colSpan={vorspaltenAnzahl} style={platzhalterStil(topQuartal)} aria-hidden="true" />
                {quartalGruppen.map((gruppe, index) => (
                    <th
                        key={`quartal-${index}`}
                        colSpan={gruppe.anzahl}
                        className="text-start"
                        style={{
                            position: "sticky",
                            top: topQuartal,
                            zIndex: 1,
                            background: "#fff",
                            borderLeft: index > 0 ? "1px solid #e9ecef" : undefined,
                            borderBottom: "1px solid #e9ecef",
                            fontSize: "0.66rem",
                            color: "#868e96",
                            fontWeight: 400,
                        }}
                    >
                        <span style={beschriftungStil}>{gruppe.label}</span>
                    </th>
                ))}
            </tr>
        </>
    );
}
